import { useCallback, useEffect } from 'react';
import {
  InfiniteData,
  keepPreviousData,
  QueryFunctionContext,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query-v5';
import { useUserSetupStore, useAuthStore } from '@sib/auth';
import { AxiosError } from 'axios';
import api from '../api';
import {
  CurrentUserDto,
  InvitedUserDto,
  InviteUserBodyDto,
  EntraGroup,
  UserOrganizationsListDto,
  UpdateProfilePictureDto,
} from '../api-client';
import { QueryKey } from '../enums';
import { PaginationOptions } from '../types';
import { buildFilter } from '../utils';

const useSetupUser = () => {
  const {
    setOrganizations,
    setUserRoles,
    setOrganization,
    organization: { id: organizationId },
    setIsSibEmployee,
    isSetupCompleted,
    setIsSetupCompleted,
  } = useUserSetupStore();

  const setupUser = useCallback(
    (data: CurrentUserDto | null | undefined) => {
      if (!data) return;

      if (isSetupCompleted) return;

      const rolesToSet = data?.roles as unknown as string[];

      const isSibEmployee =
        rolesToSet.includes(EntraGroup.Cms) ||
        rolesToSet.includes(EntraGroup.Employee);

      setUserRoles(rolesToSet);
      setIsSibEmployee(isSibEmployee);

      if (data?.organizations?.length && !isSibEmployee) {
        try {
          setOrganizations(data.organizations);

          const newOrganization = data.organizations[0];

          const shouldAssignNewOrganization =
            !data.organizations.some(
              organization => organization.id === organizationId
            ) && !isSibEmployee;
          if (shouldAssignNewOrganization) {
            setOrganization({
              name: newOrganization.name,
              id: newOrganization.id,
            });
          }

          setIsSetupCompleted(true);
        } catch {
          return setOrganization({
            id: '',
            name: '',
          });
        }
      }

      if (isSibEmployee) {
        setIsSetupCompleted(true);
      }
    },
    [
      organizationId,
      setOrganizations,
      setUserRoles,
      setOrganization,
      setIsSibEmployee,
      isSetupCompleted,
      setIsSetupCompleted,
    ]
  );

  return setupUser;
};

export const useGetUser = () => {
  const setupUser = useSetupUser();
  const { setAccount } = useAuthStore();

  const queryResult = useQuery<CurrentUserDto, AxiosError>({
    queryKey: [QueryKey.Resource.User],
    queryFn: async () => {
      const data = await api.user.usersControllerGetCurrentUser();

      setupUser(data);

      return data;
    },
  });

  useEffect(() => {
    if (queryResult.error?.response?.status === 403) {
      setAccount(null);
    }
  }, [queryResult.error, setAccount]);

  return queryResult;
};

export const useGetUserOrganizations = (
  paginationOptions: PaginationOptions,
  options?: UseQueryOptions<UserOrganizationsListDto>
): UseQueryResult<UserOrganizationsListDto> =>
  useQuery({
    placeholderData: keepPreviousData,
    ...options,
    queryKey: [
      QueryKey.Resource.UserOrganizations,
      paginationOptions.page,
      paginationOptions.filter,
    ],

    queryFn: () =>
      api.user.usersControllerGetUserOrganizations(paginationOptions),
  });

const organizationsQueryFn = async (
  { pageParam = 1 }: QueryFunctionContext<readonly unknown[], unknown>,
  { search, size, sort }: Omit<PaginationOptions, 'page'> & { search?: string }
) => {
  //TODO: Implement mechanism for current api to handle filters
  const filter = search
    ? buildFilter({
        field: 'name',
        operator: 'contains',
        value: search,
      })
    : null;
  const response = await api.user.usersControllerGetUserOrganizations({
    page: Number(pageParam),
    size,
    ...(sort && {
      sort,
    }),
    ...(filter && {
      filter: [filter],
    }),
  });

  return response;
};

export const useInfiniteOrganizations = (
  { page, search = '', size, sort }: PaginationOptions & { search?: string },
  options?: Partial<
    UseInfiniteQueryOptions<
      UserOrganizationsListDto,
      Error,
      InfiniteData<UserOrganizationsListDto>
    >
  >
) => {
  const {
    isSibEmployee,
    organization: { id: organizationId },
    setOrganization,
  } = useUserSetupStore();

  return useInfiniteQuery({
    ...options,
    queryKey: [QueryKey.Resource.UserOrganizations, search],
    queryFn: async pageParams => {
      const response = await organizationsQueryFn(pageParams, {
        size,
        search,
        ...(sort && { sort }),
      });

      if (!organizationId && isSibEmployee && response.items.length > 0) {
        const newOrganization = response.items[0];
        setOrganization({
          id: newOrganization.id,
          name: newOrganization.name,
        });
      }

      return response;
    },
    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: page || 1,
    enabled: !!isSibEmployee,
  });
};

export const useGetInvitedUsers = (options?: { enabled?: boolean }) =>
  useQuery<InvitedUserDto[]>({
    queryKey: [QueryKey.Resource.InvitedUsers],
    queryFn: async () =>
      await api.user.usersControllerGetInvitedUsers({
        withAccessToken: true,
      }),
    enabled: options?.enabled ?? true,
  });

export const useGetOrganizationInvitedUsers = (
  organizationId: string,
  options?: { enabled?: boolean }
) =>
  useQuery<InvitedUserDto[]>({
    queryKey: [QueryKey.Resource.InvitedUsers, organizationId],
    queryFn: async () =>
      await api.organization.organizationControllerGetInvitedUsers(
        organizationId,
        {
          withAccessToken: true,
        }
      ),
    enabled: options?.enabled ?? !!organizationId,
  });

//TODO: Remove types once api-client is updated
interface InvitedUser {
  createdDateTime: string;
  email: string;
  name: string;
  oid: string;
}

interface InviteUserError {
  email: string;
  error: string;
}

interface InviteUserResponse {
  success: InvitedUser[];
  failed: InviteUserError[];
}

export const useInviteUser = (
  options?: UseMutationOptions<
    InviteUserResponse,
    Error,
    InviteUserBodyDto & { organizationId: string }
  >
) =>
  useMutation({
    ...options,
    mutationFn: async ({ organizationId, ...data }) =>
      api.user.usersControllerInviteUser(organizationId, data, {
        withAccessToken: true,
      }) as unknown as Promise<InviteUserResponse>,
  });

interface DeleteInvitedUserPayload {
  userId: string;
}

export const useDeleteInvitedUser = (
  options?: UseMutationOptions<void, Error, DeleteInvitedUserPayload>
) =>
  useMutation({
    ...options,
    mutationFn: async ({ userId }) =>
      api.user.usersControllerDeleteInvitedUser(userId, {
        withAccessToken: true,
      }),
  });

interface UpdateProfilePictureResponseDto {}

export const useUploadProfilePicture = (
  options?: UseMutationOptions<
    UpdateProfilePictureResponseDto,
    Error,
    UpdateProfilePictureDto
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.User],
      });
      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: data =>
      api.profilePicture.profilePictureControllerUpdateTicket(
        data
      ) as unknown as Promise<UpdateProfilePictureResponseDto>,
  });
};
